import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { inject } from 'mobx-react';
import { Formik, Form } from 'formik';

import DialogContent from '@material-ui/core/DialogContent';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { MTableBody } from '@material-table/core';
//import TextField from '@material-ui/core/TextField';
import FormNumberField from 'components/FormFields/FormNumberField';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import MaterialTable from 'components/Table/Table';
import { formatAmountValue, sqlDateToLabel } from 'helpers/helpers';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import { AmountValue } from 'common/interfaces';
import FilterMenu from 'components/FilterMenu/FilterMenu';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import withoutEnterSubmit from 'components/withoutEnterSubmit';

import useStyles, { centerCellStyles, rightCellStyles } from '../styles/Accounts';
import { RootStore } from 'stores/rootStore';
import { API_ACCOUNT_EXPECTED_DEPOSITS, API_ACCOUNT_EXPECTED_DEPOSIT } from 'api/endpoints';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Deposit {
  period: string;
  expected: AmountValue;
  balance: AmountValue;
  detail: string[];
  paid: AmountValue;
  adjusted?: boolean;
}
interface Deposits {
  items: Deposit[];
  total: Deposit;
}

interface Props {
  handleClose: () => void;
  handleRefresh: () => void;
  accountId: string;
  rootStore?: RootStore;
}

// TODO: check c&p (periods; rankings, graph)
// TODO: periods to enum (all components)
const periods = [
  { value: 'ALL_TIME', label: 'accountDetail_graph_allTime' },
  { value: 'THIS_YEAR', label: 'accountDetail_graph_thisYear' },
  { value: 'LAST_3M', label: 'accountDetail_graph_3months' },
  { value: 'LAST_6M', label: 'accountDetail_graph_6months' },
  { value: 'LAST_YEAR', label: 'accountDetail_graph_year' },
];

const ExpectedDeposits: React.FC<Props> = ({ handleClose, handleRefresh, accountId, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [data, setData] = useState<Deposit[]>([]);
  const [total, setTotal] = useState<Deposit | null>(null);
  const [period, setPeriod] = useState('LAST_YEAR');
  const [loading, setLoading] = useState(false);
  const [depositPeriod, setDepositPeriod] = useState<string | null>(null);
  const [deposit, setDeposit] = useState('');
  const [adjusted, setAdjusted] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const response = await rootStore!.stores.apiStore.get<Deposits>(API_ACCOUNT_EXPECTED_DEPOSITS(accountId, period));
    if (response?.data) {
      setData(response.data.items.map((item) => ({ id: item.period, ...item })));
      setTotal(response.data.total);
    }
    setLoading(false);
  }, [rootStore, accountId, period]);

  useEffect(() => {
    (async () => {
      await fetchData();
      setAdjusted(false);
    })();
  }, [accountId, period, fetchData]);

  const handleCloseDeposit = () => {
    setDepositPeriod(null);
    setDeposit('');
  };

  const handleSaveDeposit = async () => {
    rootStore!.stores.uiStore.setLoading(true);
    const response = await rootStore!.stores.apiStore.post(API_ACCOUNT_EXPECTED_DEPOSIT(accountId), {
      period: depositPeriod,
      expected: { value: Number(deposit), currency: total?.expected.currency },
    });
    if (response!.status === 200) {
      handleCloseDeposit();
      setAdjusted(true);
      await fetchData();
    }
    rootStore!.stores.uiStore.setLoading(false);
  };

  return (
    <>
      <Dialog maxWidth="lg" open={true}>
        <DialogHeader
          onClose={() => {
            if (adjusted) handleRefresh();
            handleClose();
          }}
        />
        <DialogContent>
          <FilterMenu onClick={setPeriod} items={periods} currentValue={period} mobile="640" />
          <div className={classes.loadingWrapper}>
            <MaterialTable
              options={{
                header: true,
                sorting: true,
                draggable: false,
                search: false,
                toolbar: false,
                paging: true,
                pageSize: 12,
                emptyRowsWhenPaging: false,
                pageSizeOptions: [12, 24, 48],
              }}
              components={{
                Body: (props) => (
                  <>
                    <MTableBody {...props} />
                    {total && (
                      <tfoot className={classes.footer}>
                        <tr>
                          <td style={{ width: '25%', textAlign: 'left', fontWeight: 'bold' }}>
                            {t('total.title')} ({t(periods.find((p) => p.value === period)?.label || '')})
                          </td>
                          <td style={{ width: '25%', textAlign: 'center', fontSize: '19px', fontWeight: 'bold' }}>
                            <span
                              className={`${classes.balance} ${
                                total.balance.value !== 0 ? 'negative_' + (total.balance.value < 0) : ''
                              }`}
                            >
                              {formatAmountValue(total.balance.value, total.balance.currency, true)}
                            </span>
                          </td>
                          <td
                            style={{
                              width: '25%',
                              textAlign: 'center',
                              fontSize: '19px',
                              fontWeight: 'bold',
                              paddingRight: '48px',
                            }}
                          >
                            {formatAmountValue(total.expected.value, total.expected.currency)}
                          </td>
                          <td style={{ width: '25%', textAlign: 'right', fontSize: '19px', fontWeight: 'bold' }}>
                            {formatAmountValue(total.paid.value, total.paid.currency)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </>
                ),
              }}
              data={data}
              columns={[
                {
                  title: t('account_expectedDeposits_month'),
                  field: 'period',
                  defaultSort: 'desc',
                  render: ({ period }) => t(`${sqlDateToLabel(period, 'MM/yyyy')}`),
                  headerStyle: { fontSize: '19px' },
                  cellStyle: {
                    width: '25%',
                    fontWeight: 'bold',
                  },
                },
                {
                  title: t('account_expectedDeposits_balance'),
                  field: 'balance',
                  headerStyle: { textAlign: 'center', fontSize: '19px' },
                  cellStyle: {
                    width: '25%',
                    fontWeight: 'bold',
                    ...centerCellStyles,
                  },
                  customSort: (a, b) => a.balance.value - b.balance.value,
                  render: ({ balance }) => (
                    <span
                      className={`${classes.balance} ${balance.value !== 0 ? 'negative_' + (balance.value < 0) : ''}`}
                    >
                      {formatAmountValue(balance.value, balance.currency, true)}
                    </span>
                  ),
                },
                {
                  title: t('account_expectedDeposits_expected'),
                  field: 'expected',
                  headerStyle: { textAlign: 'center', fontSize: '19px', paddingRight: '48px' },
                  cellStyle: {
                    width: '25%',
                    ...centerCellStyles,
                  },
                  render: ({ expected, period, detail, adjusted }) => {
                    return (
                      <div style={{ width: '100%', position: 'relative', paddingRight: '48px' }}>
                        {' '}
                        {/*display: 'flex'*/}
                        <span>
                          <strong>{formatAmountValue(expected.value, expected.currency)}</strong>
                          {detail.length > 0 && (
                            <>
                              <br />
                              {detail.join(' + ')}
                            </>
                          )}
                          {adjusted && (
                            <>
                              <br />
                              <strong className={`${classes.balance} negative_true`}>
                                ({t('account_expectedDeposits_adjusted')})
                              </strong>
                            </>
                          )}
                        </span>
                        <IconButton
                          size="medium"
                          //style={{ alignSelf: 'center' }}
                          style={{ position: 'absolute', right: 0, top: '50%', transform: 'translate(0,-50%)' }}
                          //className="accountDetail-button"
                          onClick={(event) => {
                            setDepositPeriod(period);
                            event.stopPropagation();
                          }}
                        >
                          <EditOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </div>
                    );
                  },
                },
                {
                  title: t('account_expectedDeposits_paid'),
                  field: 'paid',
                  headerStyle: { textAlign: 'right', fontSize: '19px' },
                  cellStyle: {
                    width: '25%',
                    fontWeight: 'bold',
                    ...rightCellStyles,
                  },
                  render: ({ paid }) => formatAmountValue(paid.value, paid.currency),
                },
              ]}
            />
            {loading && (
              <div className={classes.loadingOverlay}>
                <LoadingIndicator size={56} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        title={depositPeriod ? sqlDateToLabel(depositPeriod, 'MM/yyyy') : null}
        isOpen={!!depositPeriod}
        maxWidth="xs"
        btnConfirmText="general_save"
        close={handleCloseDeposit}
        confirm={handleSaveDeposit}
        noCancelButton
      >
        <Formik initialValues={{ expectedDeposit: 0 }} enableReinitialize onSubmit={() => {}}>
          {(formikProps) => (
            <FormWithoutEnterSubmit style={{ width: '100%' }}>
              {' '}
              <FormNumberField
                decimalScale={2}
                name="expectedDeposit"
                value={deposit}
                label={t('account_expectedDeposits_edit')}
                onChange={(e) => {
                  setDeposit(e.target.value);
                }}
                endAdornment={t(`currency_${total?.expected.currency}`)}
              />
            </FormWithoutEnterSubmit>
          )}
        </Formik>
      </ConfirmDialog>
    </>
  );
};

export default inject('rootStore')(ExpectedDeposits);
